import { KiteIcon } from '@kite/react-kite';
import React from 'react';
import { SmartLink, SmartLinkProps } from '../SmartLink/SmartLink';
import {
    EventName,
    UserInteractionEvent,
    UserInteractionType,
} from '../../models/GTMEventTypes';
import { onClickEventTracker } from '../../shared/analytics/callAnalytics';
import { useParentNodeProvider } from '../../providers/ParentNodeProvider';
import resolvePath from '../../shared/resolvePath';
import './ReachButton.scss';

type ReachButtonStyle = {
    buttonStyle: `primary` | `secondary` | `borderless`;
    buttonIcon?: {
        icon: string;
        position?: `left` | `right`;
        iconSize?: string;
    };
};

type ReachButtonButton = ReachButtonStyle &
    React.DetailedHTMLProps<
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        HTMLButtonElement
    >;

type ReachButtonLink = ReachButtonStyle & SmartLinkProps;

export const ReachButton = (props: ReachButtonLink | ReachButtonButton) => {
    /** This has the handy feature of stripping buttonStyle and buttonIcon out of a new outProps object. */
    const { buttonStyle, buttonIcon, ...outProps } = props;
    const { nodeName: pNodeName, nodeType: pNodeType } =
        useParentNodeProvider();

    const InnerElem = () => (
        <span
            className={
                buttonIcon
                    ? `icon--` + buttonIcon.position
                        ? buttonIcon.position
                        : `left`
                    : undefined
            }
        >
            {buttonIcon && (
                <KiteIcon icon={buttonIcon.icon} size={buttonIcon.iconSize} />
            )}
            {props.children}
        </span>
    );

    outProps.className = [
        props.className,
        `kite-button`,
        `reach-button`,
        buttonStyle,
    ]
        .join(` `)
        .trim();

    if (`href` in outProps) {
        return (
            <SmartLink {...outProps}>
                <InnerElem />
            </SmartLink>
        );
    }

    // Populate meta data for tracking the user interaction event
    const eventAnalytics: UserInteractionEvent = {
        event: EventName.USER_INTERACTION,
        event_data: {
            type: UserInteractionType.BUTTON_CLICK,
            component_name: props.children as string,
            component_type: `Button`,
            parent_name: pNodeName,
            parent_type: pNodeType,
            current_path: resolvePath(),
            next_path: undefined,
        },
    };

    return (
        <button
            {...(outProps as ReachButtonButton)}
            onClick={(e) =>
                onClickEventTracker(e, eventAnalytics, outProps.onClick)
            }
        >
            <InnerElem />
        </button>
    );
};
